import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { TENANT_RESOLVE_URL } from "../features/UrlHelper";
import { getTenant } from "../features/tenantResolver";
import { brandingFor } from "../features/tenantDefaults";
import TenantGate from "../components/TenantGate/TenantGate";
import TenantHead from "../components/TenantGate/TenantHead";
import AutickoLoader from "../components/Auticko/AutickoLoader";

/**
 * Prevadzka, ktoru stranka zobrazuje.
 *
 * `mode`, `slug`, `basename` su z URL (features/tenantResolver.js) a su znamu
 * hned. `tenant` (id, nazov, branding, platnost) dotiahne GET /api/tenant/resolve.
 *
 * GATING: kym prevadzka nie je znama, deti sa NErenderuju. Inak by
 * DailyMenuPage / KontaktPage / AuthContext vystrelili poziadavky skor, nez
 * vieme, ci prevadzka vobec existuje -- a neznamy slug by skoncil 400-kou
 * v kazdej obrazovke namiesto jednej zrozumitelnej stranky.
 */
const TenantContext = createContext(null);

const potrebujePrevadzku = (mode) => mode === "path" || mode === "host";

export function TenantProvider({ children }) {
  const zUrl = getTenant();
  const [stav, setStav] = useState(
    potrebujePrevadzku(zUrl.mode) ? { status: "loading", tenant: null } : { status: "ready", tenant: null },
  );

  const nacitaj = useCallback(() => {
    if (!potrebujePrevadzku(zUrl.mode)) return;
    setStav({ status: "loading", tenant: null });
    axios
      .get(TENANT_RESOLVE_URL)
      .then((res) => setStav({ status: "ready", tenant: res.data?.tenant ?? null }))
      .catch((err) => {
        const code = err?.response?.status;
        setStav({ status: code === 404 || code === 400 ? "notFound" : "error", tenant: null });
      });
  }, [zUrl.mode]);

  useEffect(() => {
    nacitaj();
  }, [nacitaj]);

  const value = useMemo(
    () => ({
      ...zUrl,
      tenant: stav.tenant,
      branding: brandingFor(stav.tenant),
      reload: nacitaj,
    }),
    [zUrl, stav.tenant, nacitaj],
  );

  // Pri načítavaní prevádzky ešte nepoznáme jej farbu, takže autíčko je
  // vo farbe Rozvozy. Oneskorenie: rýchla odpoveď ním neblikne.
  let obsah = children;
  if (stav.status === "loading") obsah = <AutickoLoader celaStranka oneskorenie={300} />;
  else if (stav.status === "notFound") obsah = <TenantGate variant="notFound" />;
  else if (stav.status === "error") obsah = <TenantGate variant="error" onRetry={nacitaj} />;
  else if (stav.tenant?.expired) obsah = <TenantGate variant="expired" tenant={stav.tenant} />;

  return (
    <TenantContext.Provider value={value}>
      <TenantHead branding={value.branding} mode={zUrl.mode} />
      {obsah}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within TenantProvider");
  }
  return context;
}
